<template>
  <v-col>
    <!-- -------------------- Product Popup -------------------- -->
    <v-dialog v-model="postProductPopup" persistent max-width="900px" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="px-3 py-3" flat>
        <v-btn color="" absolute x-small icon top right @click="postProductPopup = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <div class="pa-2 font-weight-medium text-h5 text-center">
          {{
            isNewProduct
              ? $langs.Gen.add[$langs.lang]
              : $langs.Gen.edit[$langs.lang]
          }}{{ $langs.space[$langs.lang] }}{{ $langs.Gen.product[$langs.lang] }}
        </div>

        <v-form ref="form" @submit.prevent="submitProduct()">
          <v-row class="ma-0">
            <!-- Left Column -->
            <v-col cols="12" sm="6">
              <v-row>
                <!-- Product Title -->
                <v-col cols="12" class="py-0">
                  <v-text-field
                    :label="$langs.AddProduct.productName[$langs.lang]"
                    v-model="postProduct.title"
                    required
                    counter="200"
                    :rules="[(v) => !!v || 'required']"
                  ></v-text-field>
                </v-col>

                <!-- Category -->
                <v-col cols="6" class="py-0">
                  <v-select
                    :items="Type.type"
                    item-text="title"
                    item-value="path"
                    v-model="postProduct.type"
                    :label="$langs.Product.category[$langs.lang]"
                    required
                    :disabled="!isNewProduct"
                    :rules="[(v) => !!v || 'required']"
                  ></v-select>
                </v-col>

                <!-- Type 1 -->
                <v-col cols="6" class="py-0">
                  <v-autocomplete
                    :items="Type.sub1"
                    item-text="title"
                    item-value="path"
                    v-model="postProduct.s1"
                    :disabled="!postProduct.type || !isNewProduct"
                    :label="$langs.Product.type[$langs.lang]"
                    hide-no-data
                    required
                    :rules="[(v) => !!v || 'required']"
                  ></v-autocomplete>
                </v-col>

                <!-- SubType 2 -->
                <v-col
                  cols="12"
                  v-if="Type.sub2 && Type.sub2.length"
                  class="py-0"
                >
                  <v-autocomplete
                    :items="Type.sub2"
                    item-text="title"
                    item-value="path"
                    v-model="postProduct.s2"
                    :disabled="!postProduct.s1 || !isNewProduct"
                    label="Species"
                    hide-no-data
                    required
                    :rules="[(v) => !!v || 'required']"
                  ></v-autocomplete>
                </v-col>

                <!-- SubType 3 -->
                <v-col
                  cols="12"
                  v-if="Type.sub3 && Type.sub3.length"
                  class="py-0"
                >
                  <v-autocomplete
                    :items="Type.sub3"
                    item-text="title"
                    item-value="path"
                    @change="setSubType3"
                    v-model="postProduct._s3"
                    label="Genes/Traits"
                    small-chips
                    deletable-chips
                    multiple
                    :rules="[(v) => !!v || 'required']"
                  ></v-autocomplete>
                </v-col>

                <!-- SubType 4 -->
                <v-col
                  cols="12"
                  v-if="Type.sub4 && Type.sub4.length && postProduct.s3"
                  class="py-0 mb-2 s4-opt"
                >
                  <div class="text-caption grey--text text--darken-1 mb-1">
                    Gene/Trait: Options ({{ $langs.Gen.optional[$langs.lang] }})
                  </div>
                  <div
                    v-for="({ title, path, subTypes }, i) in Type.sub4"
                    :key="i"
                    style="overflow-x: auto; white-space: nowrap"
                    class="pb-1 pl-2"
                    :class="{ 's4-opt-focus': postProduct.s3[path] }"
                  >
                    <div
                      class="text-caption mr-2 py-1 d-inline-block white"
                      style="position: sticky; left: -8px; z-index: 1"
                    >
                      {{ title }} :
                    </div>
                    <v-btn-toggle
                      v-model="postProduct.s3[path]"
                      @change="(v) => setSubType4(path, v)"
                      color="primary"
                    >
                      <v-btn
                        v-for="({ title, path }, i) in subTypes"
                        :key="i"
                        :value="path"
                        :ripple="false"
                        x-small
                        >{{ title }}</v-btn
                      >
                      <v-btn
                        v-if="postProduct.s3[path]"
                        :value="0"
                        :ripple="false"
                        icon
                        x-small
                        ><v-icon x-small>mdi-close</v-icon></v-btn
                      >
                    </v-btn-toggle>
                  </div>
                </v-col>

                <template v-if="postProduct.type == 'animals'">
                  <v-col cols="12" sm="6" class="py-0">
                    <!-- Birth Date -->
                    <v-menu
                      ref="datePickerMenu"
                      v-model="datePickerMenu.active"
                      :close-on-content-click="false"
                      :return-value.sync="datePickerMenu.date"
                      transition="scale-transition"
                      offset-y
                      max-width="290px"
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="postProduct.detail._birth"
                          :label="$langs.Product.birth[$langs.lang]"
                          :hint="`(${$langs.Gen.optional[$langs.lang]})`"
                          persistent-hint
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="postProduct.detail._birth"
                        type="month"
                        no-title
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          text
                          color="primary"
                          @click="datePickerMenu.active = false"
                        >
                          Cancel
                        </v-btn>
                        <v-btn
                          text
                          color="primary"
                          @click="
                            $refs.datePickerMenu.save(datePickerMenu.date)
                          "
                        >
                          OK
                        </v-btn>
                      </v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" sm="6" class="py-0">
                    <v-text-field
                      :label="$langs.Product.weight[$langs.lang]"
                      :hint="`(${$langs.Gen.optional[$langs.lang]})`"
                      persistent-hint
                      type="number"
                      suffix="g."
                      v-model="postProduct.detail.weight"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" class="py-0">
                    <v-radio-group
                      :label="$langs.Product.sex[$langs.lang]"
                      row
                      v-model="postProduct.detail.sex"
                      :rules="[(v) => !!v || 'required']"
                    >
                      <v-radio
                        :label="$langs.Product.male[$langs.lang]"
                        value="m"
                      ></v-radio>
                      <v-radio
                        :label="$langs.Product.female[$langs.lang]"
                        value="w"
                      ></v-radio>
                      <v-radio
                        :label="$langs.Product.unknown[$langs.lang]"
                        value="o"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      :label="$langs.Product.diet[$langs.lang]"
                      :hint="`(${$langs.Gen.optional[$langs.lang]})`"
                      v-model="postProduct.detail.diet"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                </template>

                <v-col cols="12" class="py-0">
                  <v-textarea
                    no-resize
                    :label="$langs.AddProduct.productDescription[$langs.lang]"
                    counter="2000"
                    v-model="postProduct.description"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-col>

            <!-- Right Column -->
            <v-col cols="12" sm="6">
              <v-row>
                <v-col cols="6" class="py-0">
                  <v-text-field
                    :label="$langs.Product.price[$langs.lang]"
                    suffix="THB"
                    type="number"
                    v-model="postProduct.price"
                    required
                    :rules="[
                      (v) => !!v || 'required',
                      (v) => v > 0 || 'Price must greater than 0',
                    ]"
                  ></v-text-field>
                </v-col>

                <v-col cols="6" class="py-0">
                  <v-text-field
                    :label="$langs.AddProduct.priceAfterDiscount[$langs.lang]"
                    suffix="THB"
                    type="number"
                    v-model="postProduct.discountPrice"
                    :hint="`(${$langs.Gen.optional[$langs.lang]})`"
                    :rules="[
                      (v) =>
                        !v ||
                        parseInt(v) < parseInt(postProduct.price) ||
                        `Must lower than 'PRICE'`,
                      (v) =>
                        v == null ||
                        v == '' ||
                        v > 0 ||
                        'Price must greater than 0',
                    ]"
                    persistent-hint
                  ></v-text-field>
                </v-col>

                <v-col cols="6" class="py-0">
                  <v-text-field
                    type="number"
                    :label="$langs.Product.shippingPrice[$langs.lang]"
                    :hint="`(${$langs.Gen.optional[$langs.lang]})`"
                    persistent-hint
                    v-model="postProduct.shippingPrice"
                  ></v-text-field>
                </v-col>

                <v-col cols="6" class="py-0">
                  <v-text-field
                    type="number"
                    :label="$langs.Product.stock[$langs.lang]"
                    v-model="postProduct.stock"
                    hint="ใส่ 0 สำหรับขายออกแล้ว"
                    persistent-hint
                  ></v-text-field>
                </v-col>

                <!-- Images Uploader -->
                <v-col cols="12">
                  <div class="text-caption grey--text text--darken-1 my-2">
                    <template v-if="isNewProduct">{{
                      $langs.AddProduct.addImages[$langs.lang](MAX_IMAGE_COUNT)
                    }}</template>
                    {{ $langs.AddProduct.selectImage[$langs.lang] }}
                  </div>

                  <v-item-group
                    v-model="selectedImage"
                    mandatory
                    active-class="img-sel blue-grey lighten-5"
                    class="d-flex pb-2"
                    style="overflow-x: auto"
                  >
                    <v-item
                      v-for="(url, i) in imagesURL"
                      :key="i"
                      v-slot="{ active, toggle }"
                    >
                      <v-card
                        tile
                        outlined
                        class="d-flex shrink pa-1 mx-1"
                        style="transition: background 0.3s; position: relative"
                        :ripple="false"
                        @click="toggle"
                      >
                        <v-progress-linear
                          v-if="queueProgress[i]"
                          :value="queueProgress[i]"
                          height="2"
                          absolute
                          bottom
                        ></v-progress-linear>
                        <v-expand-x-transition mode="out-in">
                          <v-icon
                            v-if="active"
                            small
                            style="width: 12px; transform: translateX(-2px)"
                            >mdi-chevron-right</v-icon
                          >
                        </v-expand-x-transition>
                        <v-btn
                          v-if="isNewProduct && !queueProgress[i]"
                          @click="removeImage(i)"
                          :ripple="false"
                          x-small
                          icon
                          elevation="2"
                          class="white"
                          style="position: absolute; right: 0; top: 0"
                          ><v-icon x-small>mdi-close</v-icon></v-btn
                        >
                        <img
                          width="80"
                          height="55"
                          style="object-fit: contain; vertical-align: bottom"
                          :src="url"
                        />
                      </v-card>
                    </v-item>
                    <input
                      type="file"
                      accept="image/*"
                      ref="file"
                      hidden
                      multiple
                      @change="({ target }) => onFilesSelected(target.files)"
                    />
                    <v-card
                      v-if="
                        isNewProduct && this.imagesURL.length < MAX_IMAGE_COUNT
                      "
                      @click="$refs.file.click()"
                      tile
                      outlined
                      class="d-flex align-center py-1 px-8 mx-1"
                      style="min-height: 65px"
                      :ripple="false"
                    >
                      <v-icon>add</v-icon>
                    </v-card>
                  </v-item-group>
                </v-col>
              </v-row>

              <!-- Product Item Preview -->
              <div
                v-if="selectedImage != null && postProduct.title"
                class="text-center"
              >
                <div class="text-subtitle-2 grey--text text--darken-1">
                  {{ $langs.AddProduct.productPreview[$langs.lang] }}
                </div>
                <Item
                  class="text-left"
                  :product="{
                    ...postProduct,
                    store,
                    images: [imagesURL[selectedImage]],
                  }"
                  disabled
                ></Item>
              </div>
            </v-col>
          </v-row>

          <!-- Action Button -->
          <v-row no-gutters class="align-center" :class="$vuetify.breakpoint.xsOnly ? `pa-3` : ``">
            <v-btn
              v-if="!isNewProduct"
              :small="$vuetify.breakpoint.xsOnly"
              text
              color="error darken-2"
              class="px-2"
              :disabled="postLoading"
              @click="deleteProductPopup"
              ><v-icon small left>mdi-delete</v-icon
              >{{
                `${$langs.Gen.delete[$langs.lang]}${$langs.space[$langs.lang]}${
                  $langs.Gen.product[$langs.lang]
                }`
              }}</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              :small="$vuetify.breakpoint.xsOnly"
              text
              class="px-2"
              :disabled="postLoading"
              @click="postProductPopup = false"
              >{{ $langs.Gen.cancel[$langs.lang] }}</v-btn
            >
            <v-btn
              class="ml-2 primary"
              :small="$vuetify.breakpoint.xsOnly"
              type="submit"
              :loading="postLoading"
              :disabled="postLoading && !mapQueueCompressedFile.length"
              >{{
                isNewProduct
                  ? $langs.Gen.add[$langs.lang]
                  : $langs.Gen.edit[$langs.lang]
              }}{{ $langs.space[$langs.lang]
              }}{{ $langs.Gen.product[$langs.lang] }}</v-btn
            >
          </v-row>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- -------------------- Product Table -------------------- -->

    <v-card class="mb-6 overflow-hidden">
      <v-row no-gutters class="align-center">
        <!-- Product Type Tabs -->
        <v-tabs class="text-h4 tab-no-arrow" style="width: 0">
          <v-tab to="/mystore/product">{{ $langs.Gen.all[$langs.lang] }}</v-tab>
          <v-tab to="#animals">{{ $langs.Gen.animals[$langs.lang] }}</v-tab>
          <v-tab to="#petfoods">{{ $langs.Gen.petfoods[$langs.lang] }}</v-tab>
          <v-tab to="#supplies">{{ $langs.Gen.supplies[$langs.lang] }}</v-tab>
        </v-tabs>

        <!-- <v-btn
          class="mr-3"
          color="primary"
          small
          @click="openNewProductPopup()"
        >
          {{ $langs.AddProduct.addProduct[$langs.lang] }}
        </v-btn> -->
      </v-row>

      <v-data-table
        :headers="tableHeaders"
        :items="myProduct.products"
        :footer-props="{
          'items-per-page-options': itemsPerPageOptions,
          'items-per-page-text': $langs.Gen.rowsPerPage[$langs.lang],
        }"
        :loading="isLoadingTable"
        :server-items-length="myProduct.length"
        :options.sync="myProduct.options"
        :no-data-text="$langs.Product.noDataAvailable[$langs.lang]"
        :loading-text="$langs.Product.loadingItems[$langs.lang]"
        disable-sort
        :item-class="itemClass"
      >
        <!-- APPROVAL STATUS -->
        <template v-slot:[`item.status.valid`]="{ item }">
          <!-- <v-icon>mdi-{{ item.status.valid ? `check` : `close` }}</v-icon> -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small v-bind="attrs" v-on="on"
                >mdi-{{
                  item.status.valid
                    ? `check-bold`
                    : productStatus(item.status)
                    ? `help`
                    : `close`
                }}</v-icon
              >
            </template>
            <span v-if="item.status.valid">Your product is approved</span>
            <span v-else-if="productStatus(item.status)">Begin review</span>
            <span v-else
              >NOT approved:<br />
              {{ item.status.detail }}
            </span>
          </v-tooltip>
        </template>

        <!-- Title -->
        <template v-slot:[`item.title`]="{ item }">
          <a :href="`/${item.type}/p/${item._id}?store=true`" target="_blank">
            {{ item.title }}</a
          >
          <v-icon
            v-if="item.detail"
            small
            class="mx-1"
            :class="sex.color[item.detail.sex]"
            >{{ sex.icon[item.detail.sex] }}</v-icon
          >
        </template>

        <!-- Image -->
        <template v-slot:[`item.images`]="{ item }">
          <v-img
            :src="productThumbnailUrl(item.images[0])"
            :aspect-ratio="3 / 2"
            width="65"
          ></v-img>
        </template>

        <!-- STOCK -->
        <template v-slot:[`item.stock`]="{ item }">
          <span class="text-caption" v-if="item.stock === 0">Hold</span>
          <template v-else>
            {{ item.stock || "?" }}
          </template>
        </template>

        <!-- Price -->
        <template v-slot:[`item.price`]="{ item }">
          {{ parseInt(item.price).toLocaleString() }}
        </template>

        <!-- Hold Edit Del -->
        <template v-slot:item.actions="{ item }">
          <div style="white-space: nowrap">
            <v-btn
              @click="soldOutProduct(item)"
              icon
              small
              :style="item.stock == 0 ? 'opacity: .5' : ''"
              :disabled="isLoadingTable || item.stock == 0"
            >
              <v-icon small>mdi-package-variant-closed</v-icon>
            </v-btn>
            <v-icon
              small
              class="mx-1"
              @click="openUpdateProductPopup(item)"
              :disabled="isLoadingTable"
              >mdi-pencil</v-icon
            >
            <v-btn
              @click="deleteProduct(item)"
              icon
              small
              :disabled="isLoadingTable"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-col>
</template>

<script>
import { mapState } from "vuex";
import ImagesPreview from "../../components/base/ImagesPreview.vue";
import Item from "@/components/base/Item";
import {
  getMenus,
  productImageUrl,
  productThumbnailUrl,
  compressImages,
} from "../../services/constants";
import store from "../../services/store";
import api from "../../services/api";
import user from "../../services/user";

export default {
  props: ["store"],
  components: {
    ImagesPreview,
    Item,
  },
  data() {
    return {
      // ======================== FOR NEW/EDIT PRODUCT POPUP ========================
      MAX_IMAGE_COUNT: 4,
      strTypeTemp: "",
      Type: {
        type: [
          { title: this.$langs.Gen.animals[this.$langs.lang], path: "animals" },
          {
            title: this.$langs.Gen.petfoods[this.$langs.lang],
            path: "petfoods",
          },
          {
            title: this.$langs.Gen.supplies[this.$langs.lang],
            path: "supplies",
          },
        ],
        sub1: [],
        sub2: [],
        sub3: [],
        sub4: [],
      },
      strPostProductTemp: "", // use to reset postProduct
      strRefPostProduct: "", // use to compare the change when edit product
      postProduct: {
        title: "",
        description: "",
        price: "",
        discountPrice: "",
        shippingPrice: "",
        stock: "",
        type: "",
        s1: "",
        s2: "",
        s3: {},
        _s3: [],
        detail: {
          sex: "",
          _birth: "",
          birth: "",
          weight: "",
          diet: "",
        },
        images: [],
      },
      imagesURL: [],
      postLoading: false,
      rulePostImages: [
        (v) => !!(v && v.length) || "required",
        (v) => {
          return (
            (v && v.length) <= 4 || "Images should be equal or less 4 files!"
          );
        },
      ],
      postProductPopup: false,
      datePickerMenu: {
        active: false,
        date: "",
      },
      isNewProduct: true,
      showFileInput: true,
      selectedImage: 0,
      mapQueueCompressedFile: [],

      // ======================== FOR DATA TABLE ========================
      myProduct: {
        products: [],
        length: 0,
        options: {},
      },
      tableHeaders: [],
      itemsPerPageOptions: [15, 30, 50],
      rawTableHeaders: [
        {
          text: "",
          value: "status.valid",
          class: "pr-0",
          cellClass: "pr-0",
          width: 36,
        },
        { text: "Title", value: "title" },
        {
          text: "Image",
          value: "images",
          width: 65,
          align: "center",
          cellClass: "px-1",
        },
        { text: "Stock", value: "stock" },
        { text: "View", value: "view" },
        { text: "Price", value: "price" },
        {
          text: "Hold | Edit | Del",
          value: "actions",
          align: "center",
          class: "px-1",
        },
      ],
      sex: {
        text: {
          m: "Male",
          w: "Female",
          o: "Unknown",
        },
        icon: {
          m: "mdi-gender-male",
          w: "mdi-gender-female",
          o: "",
        },
        color: {
          m: "blue--text",
          w: "pink--text",
        },
      },
      isLoadingTable: true,
    };
  },
  mounted() {
    // this.loadProducts();
    // watch "myProduct.options" will call loadProducts when mount this
    this.strPostProductTemp = JSON.stringify(this.postProduct);
    this.strTypeTemp = JSON.stringify(this.Type);
  },
  methods: {
    async loadProducts() {
      const store = user.restore().storeInfo;
      if (!store) return;
      this.tableHeaders = [...this.rawTableHeaders];

      // Set Product Type = #{type}; (URL ANCHOR)
      const type = this.$route.hash.slice(1);

      // In all Product, Add type Column (First Column) to tableHeader
      if (type == "")
        this.tableHeaders.splice(1, 0, {
          text: "Type",
          value: "type",
          align: "center",
          cellClass: "pr-0",
          class: "pr-0",
          width: 60,
        });

      let { itemsPerPage, page } = this.myProduct.options;
      this.isLoadingTable = true;
      const { products, amount } = await api.getProductsByStore(store._id, {
        count: this.myProduct.length == 0,
        size: itemsPerPage,
        page,
        store: true,
        type,
      });
      this.isLoadingTable = false;
      if (amount) this.myProduct.length = amount;
      this.myProduct.products = products;
    },
    openNewProductPopup() {
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.isNewProduct = true;
      this.showFileInput = false;
      setTimeout(() => (this.showFileInput = true), 100);
      // Reset Type, postProduct
      this.postProduct = JSON.parse(this.strPostProductTemp);
      this.Type = JSON.parse(this.strTypeTemp);
      this.imagesURL = [];
      this.postProductPopup = true;
    },
    async openUpdateProductPopup(product) {
      product = { ...product };
      this.isNewProduct = false;

      // Cache Data
      for (let s of ["s1", "s2"])
        if (product[s] && product[s].path) product[s] = product[s].path;
      this.imagesURL = product.images.map((image) => productImageUrl(image));
      delete product.images;
      if (product.detail && typeof product.detail.birth == "string")
        product.detail._birth = product.detail.birth.slice(0, 7);
      this.postProduct = product;

      // Open Popup
      this.postProductPopup = true;
      if (this.$refs.form) this.$refs.form.resetValidation();

      // Load Full Product Info Data
      ({ data: product } = await api.getProducts(`id/${product._id}`, {
        store: true,
      }));
      for (let s of ["s1", "s2"]) if (product[s]) product[s] = product[s].path;
      this.imagesURL = product.images.map((image) => productImageUrl(image));
      product.imageName = product.images;
      delete product.images;
      if (product.detail && typeof product.detail.birth == "string")
        product.detail._birth = product.detail.birth.slice(0, 7);

      /**
       | PRODUCT SUBTYPE 3 - SUBTYPE 4
       |- [From Server]
       |    .s3 is [{ title, path, s4?: {title, path} }]
       |- [Local data]
       |    .s3 is {[path]: p4}
       |    ._s3 is [path]
       */
      if (product.s3 && product.s3.length)
        product._s3 = product.s3.map((s) => s.path);
      this.postProduct = { ...product, s3: {} };

      if (!this.Type.sub3.length) await new Promise((r) => setTimeout(r, 100)); // slowdown, waiting getMenu to get .sub3
      // This will change .s3
      this.setSubType3(product._s3);
      for (let { path, s4 } of product.s3)
        this.setSubType4(path, s4 ? s4.path : null);

      this.strRefPostProduct = JSON.stringify(this.postProduct);
    },
    async submitProduct() {
      if (!this.$refs.form.validate()) return;
      this.postLoading = true;

      if (this.isNewProduct) {
        if (!this.imagesURL.length) {
          this.postLoading = false;
          this.postProductPopup = false;
          api.makeAlert(`Please add images`, "info");
          setTimeout(() => (this.postProductPopup = true), 500);
          return;
        }
      } else {
        // for camparison old and new product info
        var refPostProduct = JSON.parse(this.strRefPostProduct);
      }

      let formData = new FormData();
      let ProductForm = `title,description,price,discountPrice,shippingPrice,stock,type,s1,s2,s3`;
      if (this.postProduct.type == "animals") ProductForm += ",detail";
      for (let key of ProductForm.split(",")) {
        if (this.postProduct[key] == undefined) continue;
        if (
          !this.isNewProduct &&
          JSON.stringify(refPostProduct[key]) ==
            JSON.stringify(this.postProduct[key])
        )
          continue;
        if (typeof this.postProduct[key] == "object")
          formData.append(key, JSON.stringify(this.postProduct[key]));
        else formData.append(key, this.postProduct[key]);
      }

      // Image Action [setFirst, delImage], (For update product)
      if (!this.isNewProduct && this.selectedImage != 0)
        formData.append(
          "imagesAction",
          JSON.stringify({ setFirst: this.selectedImage })
        );

      // For Compressd Image Files
      if (
        this.isNewProduct &&
        ((this.postProduct.images && this.postProduct.images.length) ||
          this.mapQueueCompressedFile.length)
      ) {
        while (this.mapQueueCompressedFile.length)
          await new Promise((r) => setTimeout(r, 100));
        this.postProduct.images.forEach((image) =>
          formData.append("images", image)
        );
      }

      let changeFlag = false;
      for (let _ of formData.values()) {
        changeFlag = true;
        break;
      }
      if (!changeFlag) {
        this.postLoading = false;
        this.postProductPopup = false;
        return;
      }

      let success, msg;
      if (this.isNewProduct)
        ({ success, msg } = await store.createProduct(formData));
      else
        ({ success, msg } = await store.updateProduct(
          this.postProduct._id,
          formData
        ));
      this.postLoading = false;
      this.postProductPopup = false;
      if (!success) {
        setTimeout(() => (this.postProductPopup = true), 1000);
        return api.makeAlert(`Error, ${msg}`, "error");
      }
      api.makeAlert(
        `${this.isNewProduct ? "Add" : "Update"} Product Successfully`,
        "primary"
      );
      this.loadProducts();
    },
    async soldOutProduct(item) {
      if (!item._id) return;
      if (!confirm(`ติดจอง/ขาย '${item.title}' แล้ว?`)) return;
      // Put Update to product (set stock = 0)
      let formData = new FormData();
      formData.append("stock", 0);
      this.postLoading = true;
      let { success, msg } = await store.updateProduct(item._id, formData);
      this.postLoading = false;
      if (!success) return api.makeAlert(`Error, ${msg}`, "error");
      api.makeAlert(`Mark '${item.title}' as SOLD OUT.`, "primary");
      this.loadProducts();
    },
    async deleteProductPopup() {
      if (await this.deleteProduct(this.postProduct))
        this.postProductPopup = false;
    },
    async deleteProduct(item) {
      if (!confirm(`ยืนยันที่จะลบ ?\n=> '${item.title}'`)) {
        return false;
      }
      this.isLoadingTable = true;
      let { success, msg } = await store.deleteProduct(item._id);
      this.isLoadingTable = false;
      if (!success) return api.makeAlert(`Error, ${msg}`, "error");
      api.makeAlert(`Delete Product Successfully`, "secondary");
      this.loadProducts();
      return true;
    },
    onFilesSelected([...files]) {
      files.forEach((file) => this.compressFiles([file]));
    },
    async compressFiles(files) {
      // SOMETIME NEW COMPRESS FILE AREN'T UPLOADED TO BACKEND!
      let avaiable =
        this.MAX_IMAGE_COUNT -
        (this.postProduct.images.length + this.mapQueueCompressedFile.length);
      if (avaiable <= 0) return;
      files.splice(avaiable);
      const indexes = [...Array(files.length).keys()].map(
        (e) => e + this.imagesURL.length
      );
      const key = new Date().getTime();
      files.forEach((file) => this.imagesURL.push(URL.createObjectURL(file)));
      this.mapQueueCompressedFile.push(
        ...indexes.map((i) => ({ key, i, progress: 0 }))
      );
      let that = this;
      let comFiles = await compressImages(files, {
        onProgress(p) {
          that.mapQueueCompressedFile.forEach((e) => {
            if (e.key == key) e.progress = p;
          });
        },
      });
      this.postProduct.images.push(...comFiles);
      // this.imagesURL.splice(indexes[i], 1, URL.createObjectURL(comFiles[i]));
      this.mapQueueCompressedFile = this.mapQueueCompressedFile.filter(
        (e) => e.key != key
      );
    },
    removeImage(i) {
      this.imagesURL.splice(i, 1);
      this.postProduct.images.splice(i, 1);
      this.mapQueueCompressedFile.forEach((e) => e.i > i && e.i--);
    },
    productThumbnailUrl(imageName) {
      return productThumbnailUrl(imageName);
    },
    productStatus(status) {
      // 1 : Approve
      // 0 : Not approve, Feedback
      // -1: Being review
      if (status.valid) return 1;
      if (status.detail) return 0;
      return -1;
    },
    setSubType3(p3) {
      if (!this.Type.sub3 || !p3) return;
      this.Type.sub4 = this.Type.sub3.filter(
        (sub) => sub.subTypes && p3.includes(sub.path)
      );
      let s3 = {};
      p3.forEach((path) => (s3[path] = this.postProduct.s3[path] || null));
      this.postProduct.s3 = s3;
    },
    setSubType4(p4, value) {
      if (value) this.postProduct.s3[p4] = value;
      else this.postProduct.s3[p4] = null;
    },
    itemClass(item) {
      if (item.stock === 0) return "text--disabled";
      if (this.productStatus(item.status) == 0) return "error lighten-5";
    },
  },
  computed: {
    ...mapState(["userInfo", "storeInfo"]),
    queueProgress() {
      return this.mapQueueCompressedFile.reduce(
        (obj, e) => ({ ...obj, [e.i]: e.progress }),
        {}
      );
    },
  },
  watch: {
    "postProduct.type": async function (type) {
      this.Type.sub1 = await getMenus({ type });
      this.Type.sub1.forEach((sub) => {
        sub.title = this.$langs.Product.subType[this.$langs.lang](sub);
      });
      this.postProduct.s1 = "";
    },
    "postProduct.s1": async function (p1) {
      if (!p1) return;
      let type = this.postProduct.type;
      this.Type.sub2 = await getMenus({ type, p1 });
    },
    "postProduct.s2": async function (p2) {
      let type = this.postProduct.type;
      let p1 = this.postProduct.s1;
      this.Type.sub3 = await getMenus({ type, p1, p2 });
    },
    "postProduct.detail._birth": function (birth) {
      if (!birth) return;
      let [_ret, yy, mm] = `${birth}`.match(/(\d+)-(\d+)/);
      let ts = new Date(yy, parseInt(mm), 0).getTime();
      this.postProduct.detail.birth = ts;
    },
    "$route.hash": function () {
      this.myProduct.length = 0;
      this.loadProducts();
    },
    "myProduct.options": function () {
      this.loadProducts();
    },
  },
};
</script>

<style lang="sass" scoped>
.s4-opt
  & > :not(.s4-opt-focus)
    opacity: .4

  & > :first-child
    opacity: 1

  &:hover, &:hover > div
    opacity: 1 !important
    transition-duration: .1s
  div
    transition-duration: .2s

.img-sel
  &:focus:before
    opacity: 0
</style>